import React, { useEffect, useRef } from 'react';
import { Message as MessageType } from '../../types/chat';
import { Message } from './Message';
import { AlertCircle } from 'lucide-react';
import { generateUUID } from '../../lib/utils/uuid';

interface MessageListProps {
  messages: MessageType[];
  isConnected: boolean;
  isOffline: boolean;
}

export function MessageList({ messages, isConnected, isOffline }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = React.useState(true);
  const [isScrolling, setIsScrolling] = React.useState(false);

  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  // Debounce scroll handling
  useEffect(() => {
    if (isScrolling) {
      const timer = setTimeout(() => setIsScrolling(false), 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isScrolling]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    
    const { scrollHeight, scrollTop, clientHeight } = containerRef.current;
    const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
    setAutoScroll(atBottom);
    setIsScrolling(true);
  };

  const renderDateDivider = (date: string) => {
    try {
    const messageDate = new Date(date);
    // Check if date is valid
    if (isNaN(messageDate.getTime())) {
      return undefined;
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let dateString = messageDate.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    if (messageDate.toDateString() === today.toDateString()) {
      dateString = 'Today';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      dateString = 'Yesterday';
    }

    return (
      <div className="flex items-center justify-center my-4">
        <div className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded-full">
          {dateString}
        </div>
      </div>
    );
    } catch (error) {
      console.error('Error rendering date divider:', error);
      return undefined;
    }
  };

  const groupMessagesByDate = () => {
    const groups: { date: string; messages: MessageType[] }[] = [];
    let currentDate = '';
    let currentGroup: MessageType[] = [];
    
    messages.forEach(message => {
      try {
        const messageDate = new Date(message.createdAt).toDateString();
        // Skip invalid dates
        if (messageDate === 'Invalid Date') {
          console.warn('Invalid message date:', message);
          return;
        } 
      
        if (messageDate !== currentDate) {
          if (currentGroup.length > 0) {
            groups.push({ 
              date: currentDate,
              messages: [...currentGroup],
              id: generateUUID()
            }); 
            currentGroup = [];
          }
          currentDate = messageDate;
          currentGroup = [message];
        } else {
          currentGroup.push(message);
        }
      } catch (error) {
        console.warn('Error grouping message:', error);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ 
        date: currentDate, 
        messages: [...currentGroup],
        id: generateUUID() 
      });
    }

    return groups;
  };

  return (
    <div
      ref={containerRef} 
      className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 overscroll-contain pb-[calc(56px+env(safe-area-inset-bottom))]"
      onScroll={handleScroll}
    >
      {!isConnected && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          <span className="text-amber-800">
            {isOffline
              ? 'You are offline. Messages will be sent when you reconnect.'
              : 'Connecting to chat server...'}
          </span>
        </div>
      )}

      {groupMessagesByDate().map(group => (
        <div key={group.id} className="space-y-2">
          {group.messages[0]?.createdAt && renderDateDivider(group.messages[0].createdAt)}
          {group.messages.map(message => (
            <Message
              key={message.id}
              message={message}
              isOwn={message.senderId === 'user'}
            />
          ))}
        </div>
      ))}

      {!autoScroll && !isScrolling && messages.length > 0 && (
        <button
          onClick={() => {
            setAutoScroll(true);
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="fixed bottom-[calc(8rem+env(safe-area-inset-bottom))] right-4 bg-primary-600 text-white rounded-full px-4 py-2 shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-h-[44px]"
        >
          Latest Messages
        </button>
      )}
      
      <div ref={bottomRef} />
    </div>
  );
}